// Layout.js
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineErrorOutline } from "react-icons/md";
import { clearTaskErrors } from "../Redux/Slice/TaskMasterSlice";
import Header from "../Components/Header/Header";
import Sidebar from "../Components/Sidebar/Sidebar";
import ChatDrawer from "../Components/Chat/ChatDrawer";
import socket from "../socket";
import notificationSound from "../Components/Chat/notificatonTMS.mpeg";
import {
  fetchConversations,
  fetchMembers,
  receiveMessage,
  updateUserStatus,
  updateTypingStatus,
  updateMessageReadReceipt,
  syncCurrentPermissions,
  setChatDrawerOpen,
  updateMessageReactions,
  setActiveConversation
} from "../Redux/Slice/ChatSlice";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Layout/Layout.css";
import Routing from "../RoutingAll/Routing";
import ReminderAlertModal from "../Components/Reminder/ReminderAlertModal";
import {
  triggerReminderAlert,
  updateReminderInState,
  fetchReminderStats,
  fetchDueReminderAlerts
} from "../Redux/Slice/ReminderSlice";

const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();
  const dispatch = useDispatch();

  const loginData = Cookies.get("loginData");
  const PersonData = loginData ? JSON.parse(loginData) : null;
  const currentUserId = PersonData?.data?._id;

  const { isChatDrawerOpen, activeConversation, conversations } = useSelector((state) => state.chat || { isChatDrawerOpen: false, activeConversation: null, conversations: [] });
  const isChatDrawerOpenRef = useRef(isChatDrawerOpen);
  const activeConversationRef = useRef(activeConversation);
  const conversationsRef = useRef(conversations);

  useEffect(() => {
    isChatDrawerOpenRef.current = isChatDrawerOpen;
  }, [isChatDrawerOpen]);

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Socket Connection and Global State Sync Effect
  useEffect(() => {
    if (!currentUserId) return;

    // Helper to trigger browser notifications (Service Worker compatible for mobile)
    const showLocalNotification = (title, options) => {
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
        const notificationOptions = {
          ...options,
          tag: options.tag || 'new-msg',
          renotify: true
        };

        const handleNotificationClick = (e) => {
          e.preventDefault();
          window.focus();
          if (options.data && options.data.conversationId) {
            const convId = options.data.conversationId;
            const conv = conversationsRef.current?.find(c => c._id === convId);
            if (conv) {
              dispatch(setActiveConversation(conv));
              dispatch(setChatDrawerOpen(true));
            } else {
              dispatch(fetchConversations(currentUserId)).then((action) => {
                const freshConvs = action.payload || [];
                const freshConv = freshConvs.find(c => c._id === convId);
                if (freshConv) {
                  dispatch(setActiveConversation(freshConv));
                  dispatch(setChatDrawerOpen(true));
                }
              });
            }
          }
        };

        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.ready
            .then((registration) => {
              registration.showNotification(title, notificationOptions);
            })
            .catch((err) => {
              console.error("ServiceWorker showNotification failed, using fallback:", err);
              try {
                const notification = new Notification(title, notificationOptions);
                notification.onclick = handleNotificationClick;
              } catch (e) {
                console.error("Fallback Notification constructor failed:", e);
              }
            });
        } else {
          try {
            const notification = new Notification(title, notificationOptions);
            notification.onclick = handleNotificationClick;
          } catch (e) {
            console.error("Notification constructor failed:", e);
          }
        }
      }
    };

    // Request browser permission and register Service Worker
    if (typeof window !== "undefined") {
      if ("Notification" in window) {
        if (Notification.permission === "default") {
          let permissionHandled = false;
          const handlePermission = (permission) => {
            if (permissionHandled) return;
            permissionHandled = true;
            console.log("Notification permission state determined:", permission);
            if (permission === "denied") {
              toast.warn(
                "Notification permission is blocked! Please enable notifications in your browser settings to receive real-time chat alerts.",
                { toastId: "notif-blocked", autoClose: 4000 }
              );
            }
          };

          try {
            // Try standard Promise + Callback syntax (supported by Chrome/Safari/Firefox)
            const promise = Notification.requestPermission(handlePermission);
            if (promise && typeof promise.then === 'function') {
              promise.then(handlePermission).catch((err) => {
                console.warn("Promise catch handler executed:", err);
              });
            }
          } catch (e) {
            // If the above throws, try Promise-only or Callback-only styles
            try {
              Notification.requestPermission()
                .then(handlePermission)
                .catch((err) => {
                  console.error("Promise-only permission request failed:", err);
                });
            } catch (err2) {
              try {
                Notification.requestPermission(handlePermission);
              } catch (err3) {
                console.error("All Notification permission request formats failed:", err3);
              }
            }
          }
        } else if (Notification.permission === "denied") {
          toast.warn(
            "Notification permission is blocked! Please enable notifications in your browser settings to receive real-time chat alerts.",
            { toastId: "notif-blocked", autoClose: 4000 }
          );
        }
      }

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.register("/sw.js")
          .then((reg) => {
            console.log("Service Worker registered successfully with scope:", reg.scope);
          })
          .catch((err) => {
            console.error("Service Worker registration failed:", err);
          });
      }
    }

    // Connect socket and register userId on connect/reconnect
    socket.io.opts.query = { userId: currentUserId };

    const handleConnect = () => {
      console.log("Socket connected! Registering User:", currentUserId);
      socket.emit("register", currentUserId);
      dispatch(fetchDueReminderAlerts(currentUserId));
      dispatch(fetchReminderStats({ userId: currentUserId }));
    };

    socket.on("connect", handleConnect);
    socket.connect();

    if (socket.connected) {
      handleConnect();
    }

    // Initial API fetches
    dispatch(fetchConversations(currentUserId));
    dispatch(fetchMembers());
    dispatch(fetchDueReminderAlerts(currentUserId));
    dispatch(fetchReminderStats({ userId: currentUserId }));

    // Listeners
    socket.on("new_message", (msg) => {
      dispatch(receiveMessage(msg));
      if (currentUserId) {
        dispatch(fetchConversations(currentUserId));
      }

      // Play a short synth tone on message arrival (synthesized Audio)
      const playBeep = () => {
        try {
          const audio = new Audio(notificationSound);
          audio.volume = 0.45;
          audio.play().catch((e) => {
            console.warn("Audio autoplay blocked by browser:", e);
          });
        } catch (e) {
          console.warn("Audio play error:", e);
        }
      };

      // Play beep only if message is from someone else
      if (msg.sender._id !== currentUserId) {
        playBeep();

        // Browser HTML5 notification if tab is hidden/inactive, user is on another page, or has drawer closed/viewing other chat
        const isNotOnChatPage = window.location.pathname !== "/owner-chat";
        const isDrawerClosed = !isChatDrawerOpenRef.current;
        const isDifferentConversation = !activeConversationRef.current || activeConversationRef.current._id !== msg.conversationId;
        const shouldNotify = document.hidden || isNotOnChatPage || isDrawerClosed || isDifferentConversation;

        if (shouldNotify) {
          showLocalNotification(`New message from ${msg.sender.Name}`, {
            body: msg.messageType === "text" ? msg.message : `[${msg.messageType}]`,
            icon: msg.sender.ProfilePhoto || "/logo192.png",
            data: { conversationId: msg.conversationId }
          });
        }
      }
    });

    socket.on("unread_notification", (data) => {
      // Play a short synth tone on message arrival (synthesized Audio)
      const playBeep = () => {
        try {
          const audio = new Audio(notificationSound);
          audio.volume = 0.45;
          audio.play().catch((e) => {
            console.warn("Audio autoplay blocked by browser:", e);
          });
        } catch (e) {
          console.warn("Audio play error:", e);
        }
      };

      playBeep();

      // Browser HTML5 notification if tab is hidden/inactive, user is on another page, or has drawer closed/viewing other chat
      const isNotOnChatPage = window.location.pathname !== "/owner-chat";
      const isDrawerClosed = !isChatDrawerOpenRef.current;
      const isDifferentConversation = !activeConversationRef.current || activeConversationRef.current._id !== data.conversationId;
      const shouldNotify = document.hidden || isNotOnChatPage || isDrawerClosed || isDifferentConversation;

      if (shouldNotify) {
        showLocalNotification(`New message from ${data.senderName}`, {
          body: data.messageSnippet || "Sent you a message",
          icon: "/logo192.png",
          tag: data.conversationId,
          data: { conversationId: data.conversationId }
        });
      }

      if (currentUserId) {
        dispatch(fetchConversations(currentUserId));
      }
    });

    socket.on("user_status_change", (data) => {
      dispatch(updateUserStatus(data));
    });

    socket.on("user_typing", (data) => {
      dispatch(updateTypingStatus(data));
    });

    socket.on("messages_read", (data) => {
      dispatch(updateMessageReadReceipt({ ...data, type: "read" }));
    });

    socket.on("messages_delivered", (data) => {
      dispatch(updateMessageReadReceipt({ ...data, type: "delivered" }));
    });

    socket.on("permissions_updated", (data) => {
      dispatch(syncCurrentPermissions(data));
      // Close chat drawer if Owner disables chat access
      if (data.chatEnabled === false) {
        dispatch(setChatDrawerOpen(false));
      }
    });

    socket.on("joined_new_conversation", (conv) => {
      // Join conversation room in socket directly
      socket.emit("join_conversation", conv._id);
      // Refresh conversation list
      dispatch(fetchConversations(currentUserId));
    });

    // Real-Time Reminder Alerts in Task Portal
    socket.on("reminder_alert", (reminder) => {
      console.log("🔔 Real-time reminder_alert received in task portal:", reminder);
      dispatch(triggerReminderAlert(reminder));
      if (currentUserId) {
        dispatch(fetchReminderStats({ userId: currentUserId }));
      }
      showLocalNotification(`Reminder: ${reminder.title}`, {
        body: reminder.description || `Scheduled for ${reminder.reminderTime} ${reminder.callNo ? `(Call #${reminder.callNo})` : ''}`,
        icon: "/logo192.png",
        tag: `reminder-${reminder._id}`,
      });
    });

    socket.on("reminder_created", (reminder) => {
      console.log("🔔 New reminder assigned to me:", reminder);
      if (currentUserId) {
        dispatch(fetchReminderStats({ userId: currentUserId }));
        dispatch(fetchDueReminderAlerts(currentUserId));
      }
      const creatorName = reminder.createdBy?.Name || "Team Member";
      toast.info(`🔔 New Reminder assigned by ${creatorName}: "${reminder.title}"`, {
        position: "top-right",
        autoClose: 6000,
      });
      showLocalNotification(`New Reminder: ${reminder.title}`, {
        body: `${reminder.description || reminder.reminderType} - Scheduled for ${reminder.reminderDate} at ${reminder.reminderTime}`,
        icon: "/logo192.png",
        tag: `reminder-created-${reminder._id}`,
      });
    });

    socket.on("reminder_status_change", (data) => {
      if (data?.reminder) {
        dispatch(updateReminderInState(data.reminder));
      }
      if (currentUserId) {
        dispatch(fetchReminderStats({ userId: currentUserId }));
      }
    });

    socket.on("reminder_update", () => {
      if (currentUserId) {
        dispatch(fetchReminderStats({ userId: currentUserId }));
      }
    });

    socket.on("message_reaction_updated", (data) => {
      dispatch(updateMessageReactions(data));
    });

    socket.on("session_revoked", (data) => {
      Cookies.remove("loginData");
      Cookies.remove("active_session_id");
      localStorage.removeItem("active_session_id");
      toast.error(data?.message || "Your session has been terminated or revoked.", {
        position: "top-right",
        autoClose: 3000,
      });
      setTimeout(() => {
        window.location.href = "/";
      }, 500);
    });

    // Listen to service worker postMessage events
    let messageHandler;
    if ("serviceWorker" in navigator) {
      messageHandler = (event) => {
        if (event.data && event.data.type === 'OPEN_CONVERSATION') {
          const convId = event.data.conversationId;
          const conv = conversationsRef.current?.find(c => c._id === convId);
          if (conv) {
            dispatch(setActiveConversation(conv));
            dispatch(setChatDrawerOpen(true));
          } else {
            dispatch(fetchConversations(currentUserId)).then((action) => {
              const freshConvs = action.payload || [];
              const freshConv = freshConvs.find(c => c._id === convId);
              if (freshConv) {
                dispatch(setActiveConversation(freshConv));
                dispatch(setChatDrawerOpen(true));
              }
            });
          }
        }
      };
      navigator.serviceWorker.addEventListener('message', messageHandler);
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("new_message");
      socket.off("unread_notification");
      socket.off("user_status_change");
      socket.off("user_typing");
      socket.off("messages_read");
      socket.off("messages_delivered");
      socket.off("permissions_updated");
      socket.off("joined_new_conversation");
      socket.off("message_reaction_updated");
      socket.off("session_revoked");
      socket.off("reminder_alert");
      socket.off("reminder_created");
      socket.off("reminder_status_change");
      socket.off("reminder_update");
      socket.disconnect();
      if ("serviceWorker" in navigator && messageHandler) {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      }
    };
  }, [dispatch, currentUserId]);

  // Handle initial mount query param redirection
  useEffect(() => {
    if (!currentUserId || !conversations || conversations.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const openConvId = params.get("openConv");
    if (openConvId) {
      const conv = conversations.find(c => c._id === openConvId);
      if (conv) {
        // Clear query parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);

        dispatch(setActiveConversation(conv));
        dispatch(setChatDrawerOpen(true));
      }
    }
  }, [currentUserId, conversations, dispatch]);

  useEffect(() => {
    if (location.pathname === "/TaskTimeingReport") {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const { SupportAcceptTaskError, InhousRNDAcceptTaskError } = useSelector(
    (state) => state.TaskMaster || {}
  );

  const activeError = SupportAcceptTaskError || InhousRNDAcceptTaskError;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100 font-sans">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="flex-1 flex flex-col min-w-0">
        <div className="shrink-0">
          <Header toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
          {/* <p className="flex items-center text-lg ms-4 text-red-500 m-0">
            <GoAlertFill className="me-2" /> heloooo
          </p> */}
        </div>

        <main className={`flex-1 min-w-0 px-3 lg:px-6 mb-2 ${["/InhouseRnd", "/CustomerReq", "/MainTask", "/SubTask"].includes(location.pathname) ? "overflow-hidden flex flex-col min-h-0" : "overflow-y-auto"}`}>
          <Routing />
        </main>
      </div>

      {/* Chat Drawer Side Panel */}
      <ChatDrawer />

      {/* Global Reminder Alert Modal */}
      <ReminderAlertModal />

      {/* Global Accept Task Error Modal */}
      {activeError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] w-full max-w-[420px] overflow-hidden border-[1.5px] border-[#e23b49] flex flex-col items-center pt-8 pb-6 px-6">

            {/* Red Circle with Exclamation */}
            <div className="bg-[#e23b49] rounded-full flex items-center justify-center w-[75px] h-[75px] mb-4">
              <MdOutlineErrorOutline className="text-white text-4xl" />
            </div>

            {/* Heading */}
            <h2 className="text-[22px] font-bold text-[#e23b49] mb-5 tracking-wide">Accept Failed</h2>

            {/* Error Message Box */}
            <div className="w-full border border-gray-100 bg-gray-50/50 rounded-lg p-3.5 mb-6 shadow-sm flex items-center justify-center text-center">
              <span className="text-[#334155] text-[15px] font-medium tracking-wide">
                {typeof activeError === "string" ? activeError : "Failed to accept task. Please try again."}
              </span>
            </div>

            {/* Close Button */}
            <button
              onClick={() => dispatch(clearTaskErrors())}
              className="px-8 py-2.5 bg-[#e23b49] hover:bg-[#cc3441] text-white rounded-lg font-bold text-[16px] shadow-sm transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
